import { getIcon } from "@hereby/components";
import { clearRetainedAuthToken } from "@hereby/components/analytics";
import type { WorkspaceCreateInput } from "@hereby/core";
import {
  describeUserPasswordPolicy,
  type UserPasswordPolicyLike,
  userPasswordPolicyError,
} from "@hereby/core/constants";
import { OrganizationSize, OrganizationType } from "@hereby/core/enums";
import Alpine from "alpinejs";

/**
 * 회원가입 + 워크스페이스 허브 컴포넌트 (admin 전용).
 *
 * 플로우: /signup (관리자 정보+기업명 → 인증 메일) → 메일 링크 → /signup/verify
 * (비밀번호 재입력 → account 세션) → /workspaces (허브: 무소속 빈 상태 /
 * 워크스페이스 선택 / 생성 위저드 → 완료 → [시작하기] → 직원관리).
 */

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) {
    return e.message;
  }
  return fallback;
}

/** 조직 유형 드롭다운 (system-admin 프로비저닝과 동일 옵션) */
export const ORG_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: OrganizationType.CLINIC, label: "의원" },
  { value: OrganizationType.HOSPITAL, label: "병원" },
  { value: OrganizationType.GENERAL_HOSPITAL, label: "종합병원" },
  { value: OrganizationType.TERTIARY_HOSPITAL, label: "상급종합병원" },
  { value: OrganizationType.LONG_TERM_CARE_HOSPITAL, label: "요양병원" },
  { value: OrganizationType.MENTAL_HOSPITAL, label: "정신병원" },
  { value: OrganizationType.DENTAL_HOSPITAL, label: "치과병원" },
  { value: OrganizationType.ORIENTAL_HOSPITAL, label: "한방병원" },
  { value: OrganizationType.MIDWIFERY_CENTER, label: "조산원" },
  { value: OrganizationType.OTHER, label: "기타" },
];

/** 규모(병상 수) 드롭다운 */
export const ORG_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: OrganizationSize.SMALL, label: "30병상 미만" },
  { value: OrganizationSize.MEDIUM, label: "30~99병상" },
  { value: OrganizationSize.LARGE, label: "100~299병상" },
  { value: OrganizationSize.XLARGE, label: "300병상 이상" },
];

/** /signup — 1단계: 관리자 정보 + 기업명 입력 → 인증 메일 발송 안내 */
export function signupFormComponent() {
  return {
    step: "form" as "form" | "sent",
    lastName: "",
    firstName: "",
    email: "",
    password: "",
    passwordConfirm: "",
    phone: "",
    organizationName: "",
    acceptTerms: false,
    error: "",
    isLoading: false,
    resendCooldown: 0,
    resendTimer: 0 as ReturnType<typeof setInterval> | 0,

    /** 비밀번호 칸 안내 — 병원이 없는 가입이라 기본 규칙(「8자 이상, 대문자·소문자·숫자·특수문자 포함」) */
    get passwordHint(): string {
      return describeUserPasswordPolicy();
    },

    get canSubmit(): boolean {
      return (
        !this.isLoading &&
        !!this.lastName.trim() &&
        !!this.firstName.trim() &&
        !!this.email.trim() &&
        !!this.password &&
        !!this.passwordConfirm &&
        this.acceptTerms
      );
    },

    async submit() {
      if (!this.canSubmit) {
        return;
      }
      if (this.password !== this.passwordConfirm) {
        this.error = "비밀번호가 일치하지 않습니다.";
        return;
      }
      // 아직 병원이 없는 계정 — 기본 규칙(8자 · 네 종류). 서버 signup.start 와 같은 함수
      const policyError = userPasswordPolicyError(this.password);
      if (policyError) {
        this.error = policyError;
        return;
      }
      this.error = "";
      this.isLoading = true;
      try {
        const sdk = Alpine.store("hereby").sdk;
        await sdk.signup.start({
          lastName: this.lastName.trim(),
          firstName: this.firstName.trim(),
          email: this.email.trim(),
          password: this.password,
          phone: this.phone.trim() || undefined,
          organizationName: this.organizationName.trim() || undefined,
          acceptTerms: this.acceptTerms,
        });
        this.step = "sent";
        this.startResendCooldown();
      } catch (e: unknown) {
        this.error = errorMessage(e, "가입 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      } finally {
        this.isLoading = false;
      }
    },

    async resend() {
      if (this.resendCooldown > 0) {
        return;
      }
      try {
        const sdk = Alpine.store("hereby").sdk;
        await sdk.signup.resend(this.email.trim());
        this.startResendCooldown();
      } catch {
        // 재발송 실패는 조용히 무시 (서버가 generic 응답)
      }
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0 && this.resendTimer) {
          clearInterval(this.resendTimer);
          this.resendTimer = 0;
        }
      }, 1000);
    },
  };
}

/** /signup/verify — 이메일 링크 토큰 + 비밀번호 재입력 → 인증 완료 화면 */
export function signupVerifyComponent() {
  return {
    token: "",
    password: "",
    error: "",
    isLoading: false,
    done: false,

    init() {
      this.token = new URLSearchParams(window.location.search).get("token") ?? "";
      clearRetainedAuthToken(window);
      if (!this.token) {
        this.error = "인증 링크가 올바르지 않습니다. 메일의 링크로 다시 접속해 주세요.";
      }
    },

    async submit() {
      if (!(this.token && this.password) || this.isLoading) {
        return;
      }
      this.error = "";
      this.isLoading = true;
      try {
        const store = Alpine.store("hereby");
        const result = await store.sdk.signup.verify(this.token, this.password);
        // account 세션을 스토어에 반영 — 라우터의 /signup/verify 예외 덕분에
        // 완료 화면이 유지되고, 버튼으로 허브에 진입한다.
        store.accountAuthenticated = true;
        store.account = result.account as unknown as Record<string, unknown>;
        store.workspaces = [];
        this.done = true;
      } catch (e: unknown) {
        this.error = errorMessage(e, "인증에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      } finally {
        this.isLoading = false;
      }
    },
  };
}

/** /invite — 직원 계정(Account) 초대 수락: 비밀번호 설정(신규) 또는 확인(기존 연결) */
export function inviteAcceptComponent() {
  return {
    token: "",
    loadingInfo: true,
    info: null as {
      email: string;
      employeeName: string;
      organizationName: string;
      hasExistingAccount: boolean;
      passwordPolicy?: UserPasswordPolicyLike;
    } | null,
    infoError: "",
    password: "",
    passwordConfirm: "",
    error: "",
    isLoading: false,

    async init() {
      this.token = new URLSearchParams(window.location.search).get("token") ?? "";
      clearRetainedAuthToken(window);
      if (!this.token) {
        this.infoError = "초대 링크가 올바르지 않습니다. 메일의 링크로 다시 접속해 주세요.";
        this.loadingInfo = false;
        return;
      }
      try {
        const sdk = Alpine.store("hereby").sdk;
        this.info = await sdk.invite.info(this.token);
      } catch (e: unknown) {
        this.infoError = errorMessage(e, "초대 정보를 불러오지 못했습니다.");
      } finally {
        this.loadingInfo = false;
      }
    },

    /** 비밀번호 칸 안내 — 초대한 병원 설정 기준(예: 「4자 이상」) */
    get passwordHint(): string {
      return describeUserPasswordPolicy(this.info?.passwordPolicy);
    },

    async accept() {
      if (!(this.info && this.password) || this.isLoading) {
        return;
      }
      if (!this.info.hasExistingAccount) {
        if (this.password !== this.passwordConfirm) {
          this.error = "비밀번호가 일치하지 않습니다.";
          return;
        }
        // 새 계정 규칙 = 초대한 병원의 일반 비밀번호 설정(invite.info 가 준다) — 서버와 같은 함수
        const policyError = userPasswordPolicyError(this.password, this.info.passwordPolicy);
        if (policyError) {
          this.error = policyError;
          return;
        }
      }
      this.error = "";
      this.isLoading = true;
      try {
        const store = Alpine.store("hereby");
        const result = await store.sdk.invite.accept(this.token, this.password);
        if (result.mode === "workspace") {
          store._adoptOrgSession(result as unknown as { user: Record<string, unknown> });
          // 라우터 x-effect가 "/"(직원관리)로 이동시킨다
          return;
        }
        // 다중 소속 — account 세션으로 허브에 진입
        store.accountAuthenticated = true;
        store.account = result.account as unknown as Record<string, unknown>;
        store.workspaces = [];
        await store.refreshWorkspaces();
      } catch (e: unknown) {
        this.error = errorMessage(e, "초대 수락에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      } finally {
        this.isLoading = false;
      }
    },
  };
}

/** /workspaces — 허브: 무소속 빈 상태 / 워크스페이스 선택 / 생성 위저드 */
export function workspaceHubComponent() {
  return {
    view: "list" as "list" | "wizard" | "created",
    wizardStep: 1,
    isLoading: false,
    error: "",
    typeOptions: ORG_TYPE_OPTIONS,
    sizeOptions: ORG_SIZE_OPTIONS,

    // 생성 위저드 입력 (선택 항목은 빈 값 = 서버 기본값)
    name: "",
    orgType: "",
    orgSize: "",
    country: "대한민국",
    taxId: "",
    payDay: 25,
    cutoffDay: 20,
    fiscalYearStart: 1,
    weeklyLimit: 52,
    dailyStandard: 8,

    // 생성 완료 상태 (완료 화면 → [시작하기]에서 소비)
    createdSession: null as { user: Record<string, unknown> } | null,
    createdName: "",

    // 초대 인박스 — 내 이메일로 온 유효한 초대 (허브에서 바로 수락)
    invites: [] as Record<string, unknown>[],
    acceptingInviteId: "",

    // 플랫폼 스태프: 지원 중인 조직 (유효 grant — meta account Phase B)
    grants: [] as Record<string, unknown>[],

    /** 상단 바 BI — 이 앱의 패밀리 락업(가입 화면 RegisterForm.brandLockup 과 같은 자산) */
    get brandLockup(): string {
      const s = Alpine.store("hereby");
      return getIcon(`logo-lockup-hereby-${s.linkedBrandFamily || s.brandFamily || "medical"}`);
    },

    init() {
      const store = Alpine.store("hereby");
      // 가입 직후(기업명 프리필 보유) + 소속 0개면 생성 위저드로 바로 진입
      const pending = (store.account?.pendingOrganizationName as string | undefined) ?? "";
      if (pending && store.workspaces.length === 0) {
        this.name = pending;
        this.view = "wizard";
      }
      // 허브 진입 시 목록 최신화 (다른 탭에서 생성/초대됐을 수 있음)
      store.refreshWorkspaces().catch(() => {
        /* account 세션이 아니면 라우터가 signin으로 보낸다 */
      });
      this.loadInvites();
      this.loadGrants();
    },

    async loadInvites() {
      try {
        const sdk = Alpine.store("hereby").sdk;
        this.invites = (await sdk.account.pendingInvites()) as unknown as Record<string, unknown>[];
      } catch {
        this.invites = [];
      }
    },

    // 플랫폼 스태프가 아니면 서버가 빈 배열을 반환한다
    async loadGrants() {
      try {
        const sdk = Alpine.store("hereby").sdk;
        this.grants = (await sdk.account.myGrants()) as unknown as Record<string, unknown>[];
      } catch {
        this.grants = [];
      }
    },

    async enterGrant(grantId: string) {
      if (this.isLoading) {
        return;
      }
      this.error = "";
      this.isLoading = true;
      try {
        await Alpine.store("hereby").enterGrantedWorkspace(grantId);
        // 라우터 x-effect가 "/"로 이동시킨다
      } catch (e: unknown) {
        this.error = errorMessage(e, "지원 워크스페이스 입장에 실패했습니다.");
        this.isLoading = false;
      }
    },

    async acceptInvite(inviteId: string) {
      if (this.acceptingInviteId) {
        return;
      }
      this.error = "";
      this.acceptingInviteId = inviteId;
      try {
        const store = Alpine.store("hereby");
        await store.sdk.account.acceptInvite(inviteId);
        await store.refreshWorkspaces();
        await this.loadInvites();
      } catch (e: unknown) {
        this.error = errorMessage(e, "초대 수락에 실패했습니다.");
      } finally {
        this.acceptingInviteId = "";
      }
    },

    get workspaces(): Record<string, unknown>[] {
      return Alpine.store("hereby").workspaces;
    },

    get accountEmail(): string {
      return (Alpine.store("hereby").account?.email as string | undefined) ?? "";
    },

    get accountName(): string {
      const account = Alpine.store("hereby").account;
      if (!account) {
        return "";
      }
      return `${account.lastName ?? ""}${account.firstName ?? ""}`;
    },

    openWizard() {
      const store = Alpine.store("hereby");
      if (!this.name) {
        this.name = (store.account?.pendingOrganizationName as string | undefined) ?? "";
      }
      this.wizardStep = 1;
      this.view = "wizard";
    },

    async enter(organizationId: string, ownerEntry = false) {
      if (this.isLoading) {
        return;
      }
      this.error = "";
      this.isLoading = true;
      try {
        // ownerEntry: 멤버십 없는 소유 조직 — owner 암묵 grant로 입장
        if (ownerEntry) {
          await Alpine.store("hereby").enterOwnedWorkspace(organizationId);
        } else {
          await Alpine.store("hereby").selectWorkspace(organizationId);
        }
        // 라우터 x-effect가 "/"(직원관리)로 이동시킨다
      } catch (e: unknown) {
        this.error = errorMessage(e, "워크스페이스 입장에 실패했습니다.");
        this.isLoading = false;
      }
    },

    async create() {
      if (!this.name.trim() || this.isLoading) {
        return;
      }
      this.error = "";
      this.isLoading = true;
      try {
        const input: WorkspaceCreateInput = {
          name: this.name.trim(),
          type: (this.orgType || undefined) as WorkspaceCreateInput["type"],
          size: (this.orgSize || undefined) as WorkspaceCreateInput["size"],
          country: this.country.trim() || undefined,
          taxId: this.taxId.trim() || undefined,
          payroll: { payDay: Number(this.payDay), cutoffDay: Number(this.cutoffDay) },
          fiscalYearStart: Number(this.fiscalYearStart),
          workHours: {
            weeklyLimit: Number(this.weeklyLimit),
            dailyStandard: Number(this.dailyStandard),
          },
        };
        // 스토어 반영 없이 SDK만 호출 — 완료 화면을 먼저 보여주고,
        // [시작하기]에서 세션을 채택해 직원관리로 진입한다.
        const sdk = Alpine.store("hereby").sdk;
        const result = await sdk.account.createWorkspace(input);
        this.createdSession = result as unknown as { user: Record<string, unknown> };
        this.createdName = this.name.trim();
        this.view = "created";
      } catch (e: unknown) {
        this.error = errorMessage(e, "워크스페이스 생성에 실패했습니다.");
      } finally {
        this.isLoading = false;
      }
    },

    start() {
      if (!this.createdSession) {
        return;
      }
      const store = Alpine.store("hereby");
      store._adoptOrgSession(this.createdSession);
      store.account = null;
      store.workspaces = [];
      // 라우터 x-effect가 "/"(기본 탭 employees=직원관리)로 이동시킨다
    },

    async logout() {
      await Alpine.store("hereby").accountLogout();
    },
  };
}
